import i18n from '@/i18n';
import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { onAppEvent } from '@/lib/appEvents';
import type { FriendEntry, FriendRequestEntry, FriendState } from '@/types/api';

/**
 * Friends management (request/accept/decline/unfriend) for the friends UI and
 * the profile-card action button. Mirrors useBlockedUsers: a single React
 * Query holds the snapshot, mutations invalidate it, and the live
 * `friends:changed` socket bridge refetches when the other party acts.
 *
 * Server contract (server/src/routes/friends.ts):
 *   GET    /api/me/friends            → { friends, incoming, outgoing }
 *   POST   /api/me/friends { userId } → send / auto-accept
 *   POST   /api/me/friends/:id/accept → accept incoming
 *   DELETE /api/me/friends/:id        → decline / cancel / unfriend
 */

export const FRIENDS_QUERY_KEY = ['me', 'friends'] as const;

export interface UseFriends {
  friends: FriendEntry[];
  incoming: FriendRequestEntry[];
  outgoing: FriendRequestEntry[];
  /** Count of pending incoming requests — drives a badge. */
  incomingCount: number;
  isLoading: boolean;
  isError: boolean;
  /** Relationship of a given user to me, for profile-card buttons. */
  stateOf: (userId: string) => FriendState;
  sendRequest: (userId: string) => void;
  accept: (userId: string) => void;
  /** Decline / cancel / unfriend (all the same DELETE). */
  remove: (userId: string) => void;
  /** The user id currently being mutated (per-row spinner), else null. */
  pendingId: string | null;
}

export function useFriends(): UseFriends {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: FRIENDS_QUERY_KEY,
    queryFn: () => api.listFriends(),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: FRIENDS_QUERY_KEY });
  };

  // Live updates: the other party accepting/cancelling pings us over the
  // socket, bridged to the 'friends:changed' app event in socketEvents.ts.
  useEffect(
    () =>
      onAppEvent('friends:changed', () => {
        void qc.invalidateQueries({ queryKey: FRIENDS_QUERY_KEY });
      }),
    [qc],
  );

  const sendMutation = useMutation({
    mutationFn: (userId: string) => api.sendFriendRequest(userId),
    onSuccess: invalidate,
    onError: (err) => toast.error(...errToToast(err, i18n.t('lib2.friends.sendFailed'))),
  });
  const acceptMutation = useMutation({
    mutationFn: (userId: string) => api.acceptFriendRequest(userId),
    onSuccess: invalidate,
    onError: (err) => toast.error(...errToToast(err, i18n.t('lib2.friends.acceptFailed'))),
  });
  const removeMutation = useMutation({
    mutationFn: (userId: string) => api.removeFriend(userId),
    onSuccess: invalidate,
    onError: (err) => toast.error(...errToToast(err, i18n.t('lib2.friends.updateFailed'))),
  });

  const friends = query.data?.friends ?? [];
  const incoming = query.data?.incoming ?? [];
  const outgoing = query.data?.outgoing ?? [];

  const stateOf = (userId: string): FriendState => {
    if (friends.some((f) => f.user.id === userId)) return 'friends';
    if (incoming.some((r) => r.user.id === userId)) return 'incoming';
    if (outgoing.some((r) => r.user.id === userId)) return 'outgoing';
    return 'none';
  };

  const pendingId =
    (sendMutation.isPending ? sendMutation.variables : null) ??
    (acceptMutation.isPending ? acceptMutation.variables : null) ??
    (removeMutation.isPending ? removeMutation.variables : null) ??
    null;

  return {
    friends,
    incoming,
    outgoing,
    incomingCount: incoming.length,
    isLoading: query.isLoading,
    isError: query.isError,
    stateOf,
    sendRequest: (userId: string) => sendMutation.mutate(userId),
    accept: (userId: string) => acceptMutation.mutate(userId),
    remove: (userId: string) => removeMutation.mutate(userId),
    pendingId,
  };
}
